import React from 'react';
import { ColumnType } from 'antd/lib/table';
import { CHARS } from 'app-constants/chars';
import dayjs from 'dayjs';
import i18n from 'i18n';
import { Button } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

import { WimPlatform } from 'store/wim/types';
import { VerificationCertificate } from 'store/wim/types/platforms';
import { copyToClipboard } from './helpers';
import { PlatformFieldName } from '../../types';

type PlatformInfoDataSource = {
  type: string;
  value: string | number;
};

type PlatformInfoColumn = {
  type: ColumnType<PlatformInfoDataSource>;
  value: ColumnType<PlatformInfoDataSource>;
};

export const platformInfoDataSource = (
  platform: Omit<
    WimPlatform,
    'mapPosition' | 'verificationCertificate' | 'measureValidity' | 'mediaValidity' | 'plateRecognition'
  > & {
    mapPosition: string;
    verificationCertificate: VerificationCertificate;
  }
): PlatformInfoDataSource[] =>
  Object.keys(platform).reduce<PlatformInfoDataSource[]>((acc, key) => {
    const columnValue = platform[key as keyof typeof platform];
    const excludedKeys = [
      PlatformFieldName.lanes,
      PlatformFieldName.alias,
      'measureValidity',
      'mediaValidity',
      'plateRecognition',
    ];

    if (key === PlatformFieldName.lanes || key === PlatformFieldName.alias || excludedKeys.includes(key)) {
      return acc;
    }

    if (key === PlatformFieldName.uid) {
      return acc.concat({
        type: i18n.t('infoFields.apiUuid'),
        value: columnValue as string,
      });
    }

    if (key === PlatformFieldName.token) {
      return acc.concat({
        type: i18n.t('infoFields.apiToken'),
        value: columnValue as string,
      });
    }

    if (key === PlatformFieldName.lastTransitDatetime) {
      return acc.concat({
        type: i18n.t('infoFields.lastUpdatedData'),
        value: columnValue ? dayjs(columnValue as number).format('DD.MM.YYYY / HH:mm:ss') : CHARS.mdash,
      });
    }

    if (key === PlatformFieldName.effectiveDate) {
      return acc.concat({
        type: i18n.t('infoFields.effectiveDate'),
        value: columnValue ? dayjs(columnValue as string).format('DD.MM.YYYY / HH:mm:ss') : CHARS.mdash,
      });
    }

    if (key === PlatformFieldName.mapPosition) {
      return acc.concat({
        type: i18n.t(`infoFields.coordinates`),
        value: columnValue as string,
      });
    }

    return acc.concat({
      type: i18n.t(`infoFields.${key}`),
      value: columnValue ? (columnValue as string | number) : CHARS.mdash,
    });
  }, []);

export const platformInfoColumns: PlatformInfoColumn = {
  type: {
    key: 'type',
    dataIndex: 'type',
  },
  value: {
    key: 'value',
    dataIndex: 'value',
    render: (value: string, record: any, index: number) => {
      if (
        (record.type === i18n.t('infoFields.apiUuid') && value) ||
        (record.type === i18n.t('infoFields.apiToken') && value)
      ) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="infoValueCell">
            <span>{value}</span>
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(value)} />
          </div>
        );
      }

      return value;
    },
  },
};
